import { useState, useEffect, useMemo, useRef } from "react";
import {
  RefreshCw,
  Loader2,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Settings,
  Calendar,
  Moon,
  Sun,
  Sunrise,
  Edit3,
  Save,
  X,
  Eye,
  RotateCcw,
  Shield,
  Sparkles,
  Bell,
  Calculator,
  Timer,
  Sliders,
  Plus,
  Crown,
  Lock,
} from "lucide-react";

// Utility function to format time with AM/PM using prayer-specific logic
const formatTimeWithAMPM = (time, prayerName = null) => {
  if (!time) return "-";
  
  // If already has AM/PM, return as is
  if (/[AaPp][Mm]/.test(time)) return time;
  
  // Parse time (expecting HH:MM format)
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;
  
  let period;
  let displayHours = hours;
  
  // Apply prayer-specific rules (similar to teleview images logic)
  if (prayerName === 'fajr' || prayerName === 'sunrise') {
    // Fajr and Sunrise are always AM
    period = 'AM';
    displayHours = hours % 12 || 12;
  } else if (prayerName === 'duhr' || prayerName === 'jummah') {
    // Duhr and Jummah are AM only if between 10:00-11:59, otherwise PM
    if (hours >= 10 && hours < 12) {
      period = 'AM';
      displayHours = hours;
    } else {
      period = 'PM';
      displayHours = hours > 12 ? hours - 12 : hours;
    }
  } else if (prayerName === 'asr' || prayerName === 'maghrib' || prayerName === 'isha') {
    // Asr, Maghrib, and Isha are always PM
    period = 'PM';
    displayHours = hours > 12 ? hours - 12 : hours;
  } else {
    // Default fallback for times without prayer context
    period = hours >= 12 ? 'PM' : 'AM';
    displayHours = hours % 12 || 12;
  }
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Basic time validation:
// - Accepts "H:MM" or "HH:MM" with optional AM/PM (e.g., "5:30", "05:30 AM", "12:05 pm")
// - Ensures minutes are 0-59 and hours are valid for the presence/absence of AM/PM.
// Returns a normalized string or null if invalid.
const validateAndNormalizeTime = (input) => {
  if (!input || typeof input !== "string") return null;
  const raw = input.trim();
  const m = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ap = m[3];
  if (Number.isNaN(hh) || Number.isNaN(mm) || mm < 0 || mm > 59) return null;
  if (ap) {
    if (hh < 1 || hh > 12) return null;
    const suffix = ap.toUpperCase();
    return `${hh}:${String(mm).padStart(2, "0")} ${suffix}`;
  }
  // 24-hour style
  if (hh < 0 || hh > 23) return null;
  return `${hh}:${String(mm).padStart(2, "0")}`;
};

// Validate and normalize Jummah/Khutbah times with auto AM/PM filling
// Uses the same logic as duhr: 10:00-11:59 is AM, otherwise PM
// If AM/PM is explicitly provided or time is in 24-hour format (>=13), that takes precedence
const validateAndNormalizeJummahTime = (input) => {
  if (!input || typeof input !== "string") return null;
  const raw = input.trim();
  const m = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const ap = m[3];
  if (Number.isNaN(hh) || Number.isNaN(mm) || mm < 0 || mm > 59) return null;
  
  // If AM/PM explicitly provided, use it
  if (ap) {
    if (hh < 1 || hh > 12) return null;
    const suffix = ap.toUpperCase();
    return `${hh}:${String(mm).padStart(2, "0")} ${suffix}`;
  }
  
  // 24-hour style validation
  if (hh < 0 || hh > 23) return null;
  
  // If it's clearly 24-hour format (13:00 or later), convert to 12-hour with PM
  if (hh >= 13) {
    return `${hh - 12}:${String(mm).padStart(2, "0")} PM`;
  }
  
  // Auto-fill AM/PM using duhr logic: 10:00-11:59 is AM, otherwise PM
  if (hh >= 10 && hh < 12) {
    return `${hh}:${String(mm).padStart(2, "0")} AM`;
  }
  
  // Hours 0-9 and 12 default to PM for Jummah times
  // 12 stays as 12, 0-9 stay as is (but would be unusual for Jummah)
  const displayHour = hh === 0 ? 12 : hh;
  return `${displayHour}:${String(mm).padStart(2, "0")} PM`;
};

import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import useHalalFeature from "../hooks/useHalalFeature";
import {
  checkAdmin,
  refreshMasjidPrayerTimes,
} from "../services/api";
import { auth } from "../config/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

// Constants for PRO features
const PRAYER_NAMES = ['fajr', 'duhr', 'asr', 'maghrib', 'isha'];
const PRAYER_LABELS = { fajr: 'Fajr', duhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' };

// Ramadan iqamah modes - includes NONE option to use normal rule
const RAMADAN_IQAMAH_MODES = [
  { value: 'NONE', label: 'Use Normal Rule' },
  { value: 'OFFSET', label: 'Offset' },
  { value: 'FIXED_TIME', label: 'Fixed Time' },
  { value: 'ROUNDED', label: 'Rounded' },
  { value: 'ROUNDED_WEEKLY', label: 'Rounded Weekly' },
  { value: 'SAME_AS_ADHAN', label: 'Same as Adhan' },
];

// Hardcoded Ramadan periods for years 2024-2035 (verified dates)
const RAMADAN_PERIODS = {
  2024: { hijriYear: 1445, startDate: '03-10', endDate: '04-09', startDisplay: 'March 10, 2024', endDisplay: 'April 9, 2024' },
  2025: { hijriYear: 1446, startDate: '02-28', endDate: '03-29', startDisplay: 'February 28, 2025', endDisplay: 'March 29, 2025' },
  2026: { hijriYear: 1447, startDate: '02-17', endDate: '03-19', startDisplay: 'February 17, 2026', endDisplay: 'March 19, 2026' },
  2027: { hijriYear: 1448, startDate: '02-07', endDate: '03-08', startDisplay: 'February 7, 2027', endDisplay: 'March 8, 2027' },
  2028: { hijriYear: 1449, startDate: '01-27', endDate: '02-25', startDisplay: 'January 27, 2028', endDisplay: 'February 25, 2028' },
  2029: { hijriYear: 1450, startDate: '01-15', endDate: '02-13', startDisplay: 'January 15, 2029', endDisplay: 'February 13, 2029' },
  2030: { hijriYear: 1451, startDate: '01-05', endDate: '02-03', startDisplay: 'January 5, 2030', endDisplay: 'February 3, 2030' },
  2031: { hijriYear: 1452, startDate: '12-25', endDate: '01-24', startDisplay: 'December 25, 2030', endDisplay: 'January 24, 2031' },
  2032: { hijriYear: 1453, startDate: '12-14', endDate: '01-12', startDisplay: 'December 14, 2031', endDisplay: 'January 12, 2032' },
  2033: { hijriYear: 1454, startDate: '12-03', endDate: '01-01', startDisplay: 'December 3, 2032', endDisplay: 'January 1, 2033' },
  2034: { hijriYear: 1455, startDate: '11-22', endDate: '12-21', startDisplay: 'November 22, 2033', endDisplay: 'December 21, 2033' },
  2035: { hijriYear: 1456, startDate: '11-11', endDate: '12-10', startDisplay: 'November 11, 2034', endDisplay: 'December 10, 2034' },
};

// Helper function to get Ramadan dates for a given Gregorian year
const getRamadanDatesForYear = (year) => {
  if (RAMADAN_PERIODS[year]) {
    return RAMADAN_PERIODS[year];
  }
  // Fallback approximation for years outside our lookup table
  const hijriYear = year - 579;
  return {
    hijriYear,
    startDisplay: 'Dates unavailable',
    endDisplay: 'Dates unavailable',
    startDate: '03-01',
    endDate: '03-30'
  };
};

// Prayer icon mapping
const PrayerIcons = {
  fajr: Moon,
  duhr: Sun,
  asr: Sun,
  maghrib: Sunrise,
  isha: Moon,
};

// Prayer gradient colors (works for both light and dark mode)
const prayerColors = {
  fajr: "from-indigo-500/20 to-purple-500/10 dark:from-indigo-500/20 dark:to-purple-500/10",
  duhr: "from-amber-500/20 to-yellow-500/10 dark:from-amber-500/20 dark:to-yellow-500/10",
  asr: "from-orange-500/20 to-amber-500/10 dark:from-orange-500/20 dark:to-amber-500/10",
  maghrib: "from-rose-500/20 to-pink-500/10 dark:from-rose-500/20 dark:to-pink-500/10",
  isha: "from-blue-500/20 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/10",
};

const prayerIconColors = {
  fajr: "bg-indigo-500/10 text-indigo-500",
  duhr: "bg-amber-500/10 text-amber-500",
  asr: "bg-orange-500/10 text-orange-500",
  maghrib: "bg-rose-500/10 text-rose-500",
  isha: "bg-blue-500/10 text-blue-500",
};

export default function PrayerTimes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { halalEnabled } = useHalalFeature();
  const [adminInfo, setAdminInfo] = useState(null);
  const [masjidData, setMasjidData] = useState(null);
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Management UI state
  const [activeTab, setActiveTab] = useState('view'); // 'view' | 'manage'
  const [manageSubTab, setManageSubTab] = useState('settings'); // 'settings'|'calendar'|'jummah'
  const [settings, setSettings] = useState(null);
  const [monthKey, setMonthKey] = useState(null);
  const [monthDays, setMonthDays] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [editDay, setEditDay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDays, setPreviewDays] = useState([]);
  const todayCalendarRowRef = useRef(null);
  // Jummah/Khutbah management state
  const [jummahTimes, setJummahTimes] = useState({ 1: '', 2: '' });
  const [khutbahTimes, setKhutbahTimes] = useState({ 1: '', 2: '' });
  
  // PRO features state
  const [subscription, setSubscription] = useState('basic');
  const isPro = useMemo(() => subscription === 'pro', [subscription]);
  const [ramadanDates, setRamadanDates] = useState(null);

  const API_URL = import.meta.env.VITE_API_URL;

  useEffect(() => {
    if (manageSubTab !== 'calendar' || loadingMonth || !todayCalendarRowRef.current) return;

    const frameId = requestAnimationFrame(() => {
      todayCalendarRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    return () => cancelAnimationFrame(frameId);
  }, [manageSubTab, loadingMonth, monthKey, monthDays]);

  // Fetch masjid subscription status
  const fetchMasjidSubscription = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/v1/masjid/admin/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.masjid?.subscription || 'basic');
      }
    } catch (error) {
      console.error('Error fetching masjid subscription:', error);
    }
  };

  // Fetch accurate Ramadan dates from backend
  const fetchRamadanDates = async (year) => {
    try {
      const response = await fetch(`${API_URL}/v1/hijri/ramadan-dates/${year}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.hijriYear) {
          setRamadanDates(data);
          return;
        }
      }
      // Fallback to local calculation
      setRamadanDates(getRamadanDatesForYear(year));
    } catch (error) {
      console.error('Error fetching Ramadan dates:', error);
      setRamadanDates(getRamadanDatesForYear(year));
    }
  };

  const scrollToMessages = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Helper to show a short success message
  const showSuccess = (msg) => {
    setSuccess(msg);
    scrollToMessages();
    setTimeout(() => setSuccess(null), 3000);
  };

  // Helper to load settings via the API (matches mobile app)
  async function loadSettings(masjidId) {
    try {
      if (!auth.currentUser) return null;
      const token = await auth.currentUser.getIdToken(true);
      const resp = await fetch(`${API_URL}/v1/masjid/${masjidId}/prayer-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await resp.json();
      if (data.ok) {
        // Normalize response to match settings shape used in this page
        const method = data.method || data.calculationMethod || 'ISNA';
        const asrMethod = data.asrMethod || 'Standard';
        let iqamahSettings = { prayerRules: {}, globalRules: {}, ramadanRules: { enabled: false } };
        if (data.iqamahSettings?.prayerRules) {
          const normalizeRule = (r) => {
            const rule = { ...r };
            // Default to Sunday if weekStartDay is missing for weekly rounding
            if (rule.mode === 'ROUNDED_WEEKLY' && (rule.weekStartDay === undefined || rule.weekStartDay === null)) {
              rule.weekStartDay = 0;
            }
            return rule;
          };
          // Ensure each prayer has array of rules (back-compat)
          const converted = {};
          const prayers = ['fajr','duhr','asr','maghrib','isha'];
          prayers.forEach((p) => {
            const rule = data.iqamahSettings.prayerRules[p];
            if (Array.isArray(rule)) converted[p] = rule.map(normalizeRule);
            else if (rule) converted[p] = [normalizeRule(rule)];
            else converted[p] = [{ mode: 'OFFSET', offsetMinutes: 10 }];
          });
          iqamahSettings.prayerRules = converted;
        }
        
        // Load PRO features: Global rules
        if (data.iqamahSettings?.globalRules) {
          iqamahSettings.globalRules = data.iqamahSettings.globalRules;
        }
        
        // Load PRO features: Ramadan rules
        if (data.iqamahSettings?.ramadanRules) {
          iqamahSettings.ramadanRules = data.iqamahSettings.ramadanRules;
        }
        
        return { calculationMethod: method, asrMethod, iqamahSettings };
      }
      return null;
    } catch (err) {
      console.error('loadSettings error', err);
      return null;
    }
  }

  // Load admin info and masjid data
  useEffect(() => {
    const loadData = async () => {
      if (!user?.email) return;

      try {
        setLoading(true);
        setError(null);

        // Check admin status
        const adminData = await checkAdmin(user.email);
        if (!adminData.isAdmin) {
          setError("You do not have admin privileges");
          return;
        }

        setAdminInfo(adminData);

        // Get masjid ID (for super admin, use activeMasjidId)
        const masjidId =
          adminData.role === "super"
            ? adminData.activeMasjidId
            : adminData.masjidId;

        if (!masjidId) {
          setError("No masjid assigned. Please contact system administrator.");
          return;
        }

        // Load masjid data via API
        const token = await auth.currentUser.getIdToken(true);
        const response = await fetch(`${API_URL}/v1/masjid/${masjidId}/details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (data.ok) {
          setMasjidData(data.masjid);
        } else {
          throw new Error(data.error || "Failed to load masjid data");
        }

        // Load current month's prayer times
        await loadPrayerTimes(masjidId);
        // Load prayer settings
        const s = await loadSettings(masjidId);
        if (s) setSettings(s);
        
        // Fetch PRO features data
        fetchMasjidSubscription();
        const currentYear = new Date().getFullYear();
        fetchRamadanDates(currentYear);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Reload settings when masjidData changes (covers super admin switching)
  useEffect(() => {
    if (!masjidData?.id) return;
    (async () => {
      const s = await loadSettings(masjidData.id);
      if (s) setSettings(s);
      // Also refresh subscription status
      fetchMasjidSubscription();
    })();
  }, [masjidData?.id]);

  // Load prayer times for current month
  const loadPrayerTimes = async (masjidId) => {
    try {
      if (!auth.currentUser) {
        setPrayerTimes(null);
        return;
      }
      const token = await auth.currentUser.getIdToken(true);
      // Use the correct endpoint for the current month
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const todayDay = String(now.getDate()).padStart(2, '0');
      
      const response = await fetch(`${API_URL}/v1/masjid/${masjidId}/prayer-times/${yearMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (data && data.ok && data.days) {
        // Find today's prayer times from the days array
        const todayData = data.days.find(d => d.day === todayDay);
        if (todayData) {
          setPrayerTimes(todayData);
        } else {
          setPrayerTimes(null);
        }
      } else {
        setPrayerTimes(null);
      }
    } catch (err) {
      console.error("Error loading prayer times:", err);
      setPrayerTimes(null);
    }
  };

  // Refresh prayer times
  const handleRefresh = async () => {
    if (!masjidData?.id) return;

    try {
      setRefreshing(true);
      setError(null);
      setSuccess(null);

      const result = await refreshMasjidPrayerTimes(masjidData.id);

      setSuccess(
        `Successfully refreshed prayer times for ${result.month}. Updated ${result.wrote} days.`
      );
      setTimeout(() => setSuccess(null), 4000);

      // Reload prayer times
      await loadPrayerTimes(masjidData.id);
    } catch (err) {
      console.error("Error refreshing prayer times:", err);
      setError(
        err.response?.data?.error ||
          "Failed to refresh prayer times. Please try again."
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch month data (prayer times documents under collection prayer_times_US/<masjidId>/<monthKey>/* )
  const fetchMonthData = async (masjidId, mKey) => {
    try {
      setLoadingMonth(true);
      if (!auth.currentUser) {
        setMonthDays([]);
        setLoadingMonth(false);
        return;
      }
      const token = await auth.currentUser.getIdToken(true);
      const resp = await fetch(`${API_URL}/v1/masjid/${masjidId}/prayer-times/${mKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.ok) {
        const days = (data.days || []).slice();
        // Sort by id or day field, with null-safe comparison
        days.sort((a,b) => {
          const aKey = a.id || a.day || '00';
          const bKey = b.id || b.day || '00';
          return aKey.localeCompare(bKey);
        });
        setMonthDays(days);
      } else {
        console.error('fetchMonthData api error', data.error);
        setMonthDays([]);
      }
    } catch (err) {
      console.error('fetchMonthData', err);
      setMonthDays([]);
    } finally {
      setLoadingMonth(false);
    }
  };

  // When switching to manage mode, load settings
  useEffect(() => {
    if (!masjidData?.id) return;
    (async () => {
      const s = await loadSettings(masjidData.id);
      setSettings(s);
      // set current monthKey
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth()+1).padStart(2,'0');
      const key = `${y}-${m}`;
      setMonthKey(key);
      // only fetch month data once user auth is available
      if (user) await fetchMonthData(masjidData.id, key);
    })();
  }, [masjidData]);

  // also refetch month when user becomes available
  useEffect(() => {
    if (!masjidData?.id || !monthKey || !user) return;
    (async ()=>{
      await fetchMonthData(masjidData.id, monthKey);
    })();
  }, [user, masjidData?.id, monthKey]);

  // Helper to navigate months relative to current monthKey
  const navigateMonth = async (delta) => {
    if (!masjidData?.id || !monthKey) return;
    const [yy, mm] = monthKey.split('-').map(Number);
    const date = new Date(yy, mm - 1 + delta, 1);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setMonthKey(key);
    await fetchMonthData(masjidData.id, key);
  };

  // Defensive: ensure overlay states are closed on initial mount to avoid stuck overlays
  useEffect(() => {
    setPreviewVisible(false);
    setEditDay(null);
  }, []);

  // Helper to convert AM/PM time to 24h format for input fields
  const to24h = (timeStr) => {
    if (!timeStr) return '';
    const trimmed = timeStr.trim();
    // If already in 24h format (HH:MM without AM/PM), return as is
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) return trimmed;
    // Parse AM/PM format
    const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!match) return trimmed;
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toLowerCase();
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;
    return `${hours}:${minutes}`;
  };

  // Prefill jummah/khutbah times from existing data in monthDays (matches app)
  // Only run once when monthDays loads, not on every state change
  useEffect(() => {
    try {
      if (!Array.isArray(monthDays) || monthDays.length === 0) return;
      const SLOTS = ['1', '2'];
      const newJummah = { ...jummahTimes };
      const newKhutbah = { ...khutbahTimes };
      let jUpdated = false;
      let kUpdated = false;
      SLOTS.forEach((slot) => {
        const existingJ = monthDays.find((d) => d?.jummah && typeof d.jummah[slot] === 'string' && d.jummah[slot].trim());
        const existingK = monthDays.find((d) => d?.khutbah && typeof d.khutbah[slot] === 'string' && d.khutbah[slot].trim());
        // Only prefill if field is still at initial empty state (user hasn't interacted)
        if (newJummah[slot] === '' && existingJ) {
          newJummah[slot] = to24h(existingJ.jummah[slot].trim());
          jUpdated = true;
        }
        if (newKhutbah[slot] === '' && existingK) {
          newKhutbah[slot] = to24h(existingK.khutbah[slot].trim());
          kUpdated = true;
        }
      });
      if (jUpdated) setJummahTimes(newJummah);
      if (kUpdated) setKhutbahTimes(newKhutbah);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthDays]); // Only depend on monthDays, not on the state values themselves

  // Whether this masjid is managed in our DB (matches app logic)
  const canManage = Boolean(adminInfo?.isAdmin && masjidData?.source === 'muslim');

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl" />
            <Loader2 className="relative h-16 w-16 animate-spin text-primary" />
          </div>
          <p className="text-lg text-muted-foreground">Loading prayer times...</p>
        </div>
      </AppLayout>
    );
  }

  const saveSettings = async (masjidId, newSettings) => {
    try {
      setSaving(true);
      if (!auth.currentUser) {
        throw new Error('Not authenticated');
      }
      
      // Validate and normalize date formats in iqamah rules
      const validateAndNormalizeDates = (settings) => {
        const normalized = JSON.parse(JSON.stringify(settings)); // deep clone
        const prayers = ['fajr', 'duhr', 'asr', 'maghrib', 'isha'];
        
        for (const prayer of prayers) {
          const rules = normalized?.iqamahSettings?.prayerRules?.[prayer] || [];
          for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];
            
            // Validate and normalize startDate
            if (rule.startDate) {
              let dateStr = rule.startDate.trim();
              // Strip any YYYY- prefix (e.g., "2024-12-15" or "2024-12/15" becomes "12-15" or "12/15")
              dateStr = dateStr.replace(/^\d{4}[-\/]/, '');
              
              // Check if it's MM/DD or MM-DD format
              const match = dateStr.match(/^(\d{1,2})[\/-](\d{1,2})$/);
              if (!match) {
                throw new Error(`Invalid date format for ${prayer} rule ${i + 1} start date. Use MM/DD format (e.g., 12/15)`);
              }
              const m = parseInt(match[1], 10);
              const d = parseInt(match[2], 10);
              if (m < 1 || m > 12) {
                throw new Error(`Invalid month in ${prayer} rule ${i + 1} start date: ${m}. Must be 1-12.`);
              }
              if (d < 1 || d > 31) {
                throw new Error(`Invalid day in ${prayer} rule ${i + 1} start date: ${d}. Must be 1-31.`);
              }
              // Normalize to MM-DD format
              rule.startDate = `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            }
            
            // Validate and normalize endDate
            if (rule.endDate) {
              let dateStr = rule.endDate.trim();
              // Strip any YYYY- prefix (e.g., "2024-12-15" or "2024-12/15" becomes "12-15" or "12/15")
              dateStr = dateStr.replace(/^\d{4}[-\/]/, '');
              
              // Check if it's MM/DD or MM-DD format
              const match = dateStr.match(/^(\d{1,2})[\/-](\d{1,2})$/);
              if (!match) {
                throw new Error(`Invalid date format for ${prayer} rule ${i + 1} end date. Use MM/DD format (e.g., 12/15)`);
              }
              const m = parseInt(match[1], 10);
              const d = parseInt(match[2], 10);
              if (m < 1 || m > 12) {
                throw new Error(`Invalid month in ${prayer} rule ${i + 1} end date: ${m}. Must be 1-12.`);
              }
              if (d < 1 || d > 31) {
                throw new Error(`Invalid day in ${prayer} rule ${i + 1} end date: ${d}. Must be 1-31.`);
              }
              // Normalize to MM-DD format
              rule.endDate = `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            }

            // Ensure weekly rounding has an explicit start-of-week value
            if (rule.mode === 'ROUNDED_WEEKLY' && (rule.weekStartDay === undefined || rule.weekStartDay === null)) {
              rule.weekStartDay = 0;
            }
          }
        }

        return normalized;
      };

      const validatedSettings = validateAndNormalizeDates(newSettings);
      const token = await auth.currentUser.getIdToken(true);
      
      // Transform settings to match backend API expectations
      // Backend expects: { method, asrMethod, iqamahSettings }
      // We store locally as: { calculationMethod, asrMethod, iqamahSettings }
      const apiPayload = {
        method: validatedSettings.calculationMethod || validatedSettings.method,
        asrMethod: validatedSettings.asrMethod,
        iqamahSettings: validatedSettings.iqamahSettings,
      };
      
      // Use API endpoint to save settings
      const response = await fetch(`${API_URL}/v1/masjid/${masjidId}/prayer-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      const data = await response.json();

      // Check for success - accept if response.ok OR data.ok is truthy
      // Backend sometimes returns 500 with { ok: true } in body
      if (!response.ok && !data.ok) {
        // If we have a specific error message, use it
        if (data.error || data.message) {
          throw new Error(data.error || data.message);
        }
        // Otherwise it might have succeeded - check if data looks empty/invalid
        if (Object.keys(data).length === 0) {
          throw new Error(`Server error: ${response.status}`);
        }
        // If data has content but no explicit ok/error, assume success (defensive)
        console.warn('Ambiguous response - assuming success:', data);
      }
      
      setSettings(validatedSettings);
      setSaving(false);
      
      // Show success message
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      return true;
    } catch (err) {
      console.error('saveSettings error', err);
      setError(err.message || 'Failed to save settings');
      setTimeout(() => setError(null), 5000);
      setSaving(false);
      return false;
    }
  };

  // Iqamah rules helpers
  const addIqamahRule = (prayer) => {
    setSettings(prev => {
      const s = prev ? { ...prev } : { iqamahSettings: { prayerRules: {} } };
      s.iqamahSettings = s.iqamahSettings || { prayerRules: {} };
      s.iqamahSettings.prayerRules = s.iqamahSettings.prayerRules || {};
      const arr = s.iqamahSettings.prayerRules[prayer] ? [...s.iqamahSettings.prayerRules[prayer]] : [];
      arr.push({ mode: 'OFFSET', offsetMinutes: 10, startDate: null, endDate: null });
      s.iqamahSettings.prayerRules[prayer] = arr;
      return s;
    });
  };

  const updateIqamahRule = (prayer, index, field, value) => {
    setSettings(prev => {
      if (!prev) return prev;
      const s = { ...prev };
      s.iqamahSettings = s.iqamahSettings || { prayerRules: {} };
      const arr = (s.iqamahSettings.prayerRules[prayer] || []).map((r,i)=> i===index ? { ...r, [field]: value } : r);
      s.iqamahSettings.prayerRules[prayer] = arr;
      return s;
    });
  };

  const removeIqamahRule = (prayer, index) => {
    setSettings(prev => {
      if (!prev) return prev;
      const s = { ...prev };
      const arr = (s.iqamahSettings.prayerRules[prayer] || []).filter((_,i)=>i!==index);
      s.iqamahSettings.prayerRules[prayer] = arr;
      return s;
    });
  };

  // PRO feature: Update global rule (min/max times)
  const updateGlobalRule = (prayer, field, value) => {
    setSettings(prev => {
      if (!prev) return prev;
      const s = JSON.parse(JSON.stringify(prev));
      s.iqamahSettings = s.iqamahSettings || {};
      s.iqamahSettings.globalRules = s.iqamahSettings.globalRules || {};
      s.iqamahSettings.globalRules[prayer] = s.iqamahSettings.globalRules[prayer] || {};
      s.iqamahSettings.globalRules[prayer][field] = value;
      return s;
    });
  };

  // PRO feature: Update Ramadan rule
  const updateRamadanRule = (prayer, field, value) => {
    setSettings(prev => {
      if (!prev) return prev;
      const s = JSON.parse(JSON.stringify(prev));
      s.iqamahSettings = s.iqamahSettings || {};
      s.iqamahSettings.ramadanRules = s.iqamahSettings.ramadanRules || { enabled: false };
      s.iqamahSettings.ramadanRules[prayer] = s.iqamahSettings.ramadanRules[prayer] || { mode: 'NONE' };
      s.iqamahSettings.ramadanRules[prayer][field] = value;
      return s;
    });
  };

  // PRO feature: Toggle Ramadan rules enabled
  const toggleRamadanEnabled = (enabled) => {
    setSettings(prev => {
      if (!prev) return prev;
      const s = JSON.parse(JSON.stringify(prev));
      s.iqamahSettings = s.iqamahSettings || {};
      s.iqamahSettings.ramadanRules = s.iqamahSettings.ramadanRules || {};
      s.iqamahSettings.ramadanRules.enabled = enabled;
      return s;
    });
  };

  const handleRecalculate = async () => {
    if (!masjidData?.id) return;
    try {
      setSaving(true);
      if (!auth.currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await auth.currentUser.getIdToken(true);
      
      const res = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/recalculate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ skipLockedDays: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to recalculate');
      setSuccess(`Recalculated: ${data.wrote || 0} days`);
      await fetchMonthData(masjidData.id, monthKey);
    } catch (err) {
      console.error('Recalculate error:', err);
      setError(err.message || 'Failed to recalculate');
      setTimeout(()=>setError(null),5000);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!masjidData?.id) return;
    try {
      setLoadingPreview(true);
      setPreviewVisible(true);
      setPreviewDays([]);
      if (!auth.currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await auth.currentUser.getIdToken(true);
      
      const res = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/preview`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ days: 7 }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPreviewVisible(false);
        throw new Error(data.error || 'Failed to generate preview');
      }
      setPreviewDays(data.days || []);
    } catch (error) {
      console.error('Preview error:', error);
      setError(error.message || 'Failed to generate preview');
      setTimeout(()=>setError(null),5000);
      setPreviewVisible(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Open edit modal for a day
  const openEditDay = (day) => {
    setEditDay(day);
  };

  const saveDay = async (masjidId, monthKey, dayId, dayData) => {
    try {
      setSaving(true);
      const token = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_URL}/v1/masjid/${masjidId}/prayer-times/${monthKey}/${dayId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dayData),
      });
      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || 'Failed to save day');
      }
      showSuccess("Prayer times updated successfully");
      scrollToMessages();
      await fetchMonthData(masjidId, monthKey);
      setSaving(false);
      setEditDay(null);
      return true;
    } catch (err) {
      console.error('saveDay error', err);
      setError(err.message || 'Failed to save day');
      setTimeout(() => setError(null), 4000);
      scrollToMessages();
      setSaving(false);
      setEditDay(null);
      return false;
    }
  };

  // Format calendar date helper (matches app)
  const formatCalendarDate = (day) => {
    try {
      if (!monthKey || !day) return day?.id || '';
      const [yearStr, monthStr] = monthKey.split('-');
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      const dayNum = parseInt(day.day || day.id, 10);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(dayNum)) {
        return day.id || day.day || '';
      }
      const d = new Date(year, month - 1, dayNum);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return day?.id || day?.day || '';
    }
  };

  // Format month display name
  const formatMonthDisplay = (key) => {
    if (!key) return '';
    const [year, month] = key.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <AppLayout>
      <div className="relative min-h-screen">
        {/* Background - subtle gold gradient */}
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#bf9a2f]/8 via-[#d4a84b]/5 to-background" />
          <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-[#bf9a2f]/10 blur-3xl" />
          <div className="absolute right-1/4 bottom-0 h-80 w-80 rounded-full bg-[#d4a84b]/8 blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-6 sm:py-6 lg:px-8">
          {/* Header Section */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight sm:text-2xl lg:text-3xl">Prayer Times</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage and sync prayer schedules
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Only show Sync Times if masjidal_id is available */}
              {masjidData?.masjidal_id && (
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                  <span className="hidden sm:inline">{refreshing ? "Syncing..." : "Sync Times"}</span>
                  <span className="sm:hidden">{refreshing ? "..." : "Sync"}</span>
                </Button>
              )}
              {canManage && (
                <Button
                  variant={activeTab === 'manage' ? 'default' : 'outline'}
                  onClick={() => setActiveTab(activeTab === 'manage' ? 'view' : 'manage')}
                  className="gap-2"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Manage</span>
                </Button>
              )}
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <Card className="border-destructive/50 bg-destructive/10">
              <CardContent className="flex items-center gap-3 p-3 sm:p-4">
                <AlertCircle className="h-5 w-5 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          )}

          {success && (
            <Card className="border-emerald-500/50 bg-emerald-500/10">
              <CardContent className="flex items-center gap-3 p-3 sm:p-4">
                <Clock className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm text-emerald-700 dark:text-emerald-300">{success}</p>
              </CardContent>
            </Card>
          )}

          {!canManage && adminInfo?.isAdmin && (
            <Card className="border-amber-500/50 bg-amber-500/10">
              <CardContent className="flex items-center gap-3 p-3 sm:p-4">
                <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Management tools are available only for masajid registered with Muslim+.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Main Content */}
          {activeTab !== 'manage' ? (
            <>
              {/* Today's Prayer Times Card */}
              <Card className="relative overflow-hidden border-none bg-gradient-to-r from-card via-card to-card/80 shadow-xl">
                <div className="absolute -right-20 -top-20 hidden h-64 w-64 rounded-full bg-primary/10 blur-3xl sm:block" />
                
                <CardContent className="relative p-4 sm:p-6 lg:p-8">
                  <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold sm:text-xl">Today's Prayer Times</h2>
                      {prayerTimes?.islamicDate && (
                        <p className="mt-1 text-sm text-muted-foreground">{prayerTimes.islamicDate}</p>
                      )}
                    </div>
                  </div>

                  {prayerTimes ? (
                    <div className="space-y-4 sm:space-y-6">
                      {/* Prayer Cards Grid */}
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
                        {[
                          { key: 'fajr', name: 'Fajr', data: prayerTimes.fajr },
                          { key: 'duhr', name: 'Dhuhr', data: prayerTimes.duhr },
                          { key: 'asr', name: 'Asr', data: prayerTimes.asr },
                          { key: 'maghrib', name: 'Maghrib', data: prayerTimes.maghrib },
                          { key: 'isha', name: 'Isha', data: prayerTimes.isha },
                        ].map(({ key, name, data }) => {
                          const IconComponent = PrayerIcons[key];
                          return (
                            <Card
                              key={key}
                              className={`relative overflow-hidden border-none bg-gradient-to-br ${prayerColors[key]} shadow-lg`}
                            >
                              <CardContent className="p-3 sm:p-4">
                                <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl ${prayerIconColors[key]} ring-1 ring-border/50 sm:mb-3 sm:h-10 sm:w-10`}>
                                  <IconComponent className="h-4 w-4 sm:h-5 sm:w-5" />
                                </div>
                                <p className="text-xs font-medium text-muted-foreground sm:text-sm">{name}</p>
                                <p className="mt-0.5 text-lg font-bold sm:mt-1 sm:text-xl lg:text-2xl">
                                  {formatTimeWithAMPM(data?.adhan, key)}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  Iqamah: {formatTimeWithAMPM(data?.iqama, key)}
                                </p>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Additional Info Row */}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                        {/* Sunrise */}
                        {prayerTimes.sunrise && (
                          <Card className="col-span-1 sm:col-span-2 border-none bg-gradient-to-br from-amber-500/20 to-orange-500/10 shadow-lg">
                            <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500 ring-1 ring-border/50 sm:h-12 sm:w-12">
                                <Sunrise className="h-5 w-5 sm:h-6 sm:w-6" />
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground sm:text-sm">Sunrise</p>
                                <p className="text-base font-semibold sm:text-lg">
                                  {formatTimeWithAMPM(prayerTimes.sunrise, "sunrise")}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Jummah Times */}
                        <Card className="col-span-1 sm:col-span-2 border-none bg-gradient-to-br from-emerald-500/20 to-teal-500/10 shadow-lg">
                          <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500 ring-1 ring-border/50 sm:h-12 sm:w-12">
                              <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground sm:text-sm">Jummah Prayer</p>
                              {["1", "2"]
                                .map((slot) =>
                                  prayerTimes?.jummah?.[slot]
                                    ? formatTimeWithAMPM(prayerTimes.jummah[slot], "jummah")
                                    : null
                                )
                                .filter(Boolean).length > 0 ? (
                                <div className="mt-2 space-y-2">
                                  {["1", "2"]
                                    .map((slot) => {
                                      const jummahTime = prayerTimes?.jummah?.[slot];
                                      const khutbahTime = prayerTimes?.khutbah?.[slot];
                                      if (!jummahTime) return null;
                                      return {
                                        slot,
                                        label: slot === "1" ? "First Prayer" : "Second Prayer",
                                        jummah: formatTimeWithAMPM(jummahTime, "jummah"),
                                        khutbah: khutbahTime ? formatTimeWithAMPM(khutbahTime, "jummah") : null,
                                      };
                                    })
                                    .filter(Boolean)
                                    .map((entry, idx) => (
                                      <div
                                        key={`${entry.label}-${idx}`}
                                        className="flex flex-wrap items-baseline gap-x-4 gap-y-1"
                                      >
                                        <span className="text-sm text-muted-foreground">{entry.label}:</span>
                                        {entry.khutbah && (
                                          <span className="text-sm text-muted-foreground">
                                            Khutbah: <span className="font-semibold text-foreground">{entry.khutbah}</span>
                                          </span>
                                        )}
                                        <span className="text-sm text-muted-foreground">
                                          {entry.khutbah ? <>Prayer: <span className="font-semibold text-foreground">{entry.jummah}</span></> : <span className="font-semibold text-foreground">{entry.jummah}</span>}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              ) : (
                                <p className="mt-1 text-sm text-muted-foreground">
                                  No Jummah times set
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center sm:py-12">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted sm:h-16 sm:w-16">
                        <Clock className="h-7 w-7 text-muted-foreground sm:h-8 sm:w-8" />
                      </div>
                      <p className="mt-4 font-medium">No prayer times available for today</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {masjidData?.masjidal_id ? 'Click "Sync Times" to load prayer times' : 'Prayer times sync is not available for this masjid'}
                      </p>
                    </div>
                  )}

                </CardContent>
              </Card>
            </>
          ) : (
            /* Management Section - Redesigned with TVSettingsDialog patterns */
            <div className="space-y-6">
              {/* Sub-tabs - Card-style selection */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'settings', label: 'Settings', icon: Sliders, description: 'Calculation & iqamah rules' },
                  { id: 'calendar', label: 'Calendar', icon: Calendar, description: 'Daily prayer times' },
                  { id: 'jummah', label: 'Jummah', icon: Moon, description: 'Friday prayer times' },
                ].map(({ id, label, icon: Icon, description }) => (
                  <button
                    key={id}
                    onClick={() => {
                      if (id === 'calendar') {
                        const now = new Date();
                        setMonthKey(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
                      }
                      setManageSubTab(id);
                    }}
                    className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-all ${
                      manageSubTab === id
                        ? 'border-primary bg-primary/10 shadow-md'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`rounded-lg p-2.5 ${manageSubTab === id ? 'bg-primary/20' : 'bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${manageSubTab === id ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${manageSubTab === id ? 'text-primary' : ''}`}>{label}</p>
                      <p className="hidden text-xs text-muted-foreground sm:block">{description}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Settings Tab - Redesigned */}
              {manageSubTab === 'settings' && (
                <div className="space-y-6">
                  {/* Calculation Method Section */}
                  <section className="rounded-xl border bg-card p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2.5">
                        <Calculator className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Calculation Method</h3>
                        <p className="text-sm text-muted-foreground">Choose how adhan times are calculated</p>
                      </div>
                    </div>
                    
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Fajr & Isha Method</Label>
                        <Select value={settings?.calculationMethod || 'ISNA'} onValueChange={(val) => setSettings({...settings, calculationMethod: val})}>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ISNA">ISNA (North America)</SelectItem>
                            <SelectItem value="MWL">Muslim World League</SelectItem>
                            <SelectItem value="Egypt">Egyptian Authority</SelectItem>
                            <SelectItem value="Karachi">University of Karachi</SelectItem>
                            <SelectItem value="Tehran">Institute of Tehran</SelectItem>
                            <SelectItem value="Makkah">Umm al-Qura, Makkah</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Asr Calculation</Label>
                        <Select value={settings?.asrMethod || 'Standard'} onValueChange={(val) => setSettings({...settings, asrMethod: val})}>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select Asr method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Standard">Standard (Shafi'i, Maliki, Hanbali)</SelectItem>
                            <SelectItem value="Hanafi">Hanafi</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </section>

                  {/* Iqamah Rules Section */}
                  <section className="rounded-xl border bg-card p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2.5">
                        <Timer className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Iqamah Rules</h3>
                        <p className="text-sm text-muted-foreground">Configure when iqamah is called for each prayer</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {['fajr','duhr','asr','maghrib','isha'].map(prayer => (
                        <IqamahRuleEditor
                          key={prayer}
                          prayer={prayer}
                          rules={settings?.iqamahSettings?.prayerRules?.[prayer] || []}
                          updateRule={updateIqamahRule}
                          addRule={addIqamahRule}
                          removeRule={removeIqamahRule}
                        />
                      ))}
                    </div>
                  </section>

                  {/* PRO: Global Min/Max Rules Section */}
                  <GlobalRulesSection
                    isPro={isPro}
                    globalRules={settings?.iqamahSettings?.globalRules}
                    updateGlobalRule={updateGlobalRule}
                  />

                  {/* PRO: Ramadan Rules Section */}
                  <RamadanRulesSection
                    isPro={isPro}
                    ramadanRules={settings?.iqamahSettings?.ramadanRules}
                    ramadanDates={ramadanDates}
                    updateRamadanRule={updateRamadanRule}
                    toggleRamadanEnabled={toggleRamadanEnabled}
                  />

                  {/* Action Buttons - Clean footer style */}
                  <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-muted/30 p-4">
                    <Button
                      onClick={async () => {
                        if (!masjidData?.id) return;
                        const success = await saveSettings(masjidData.id, settings);
                        if (success) {
                          await handleRecalculate();
                          if (monthKey) {
                            await fetchMonthData(masjidData.id, monthKey);
                          }
                        }
                      }}
                      disabled={saving}
                      className="gap-2"
                    >
                      <Save className="h-4 w-4" />
                      Save & Apply
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      disabled={loadingPreview}
                      className="gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      {loadingPreview ? 'Loading...' : 'Preview 7 Days'}
                    </Button>
                    <div className="flex-1" />
                    <Button
                      variant="ghost"
                      onClick={async () => { if (!masjidData?.id) return; const s = await loadSettings(masjidData.id); setSettings(s); }}
                      className="gap-2 text-muted-foreground"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Reset
                    </Button>
                  </div>
                </div>
              )}

              {/* Calendar Tab - Redesigned */}
              {manageSubTab === 'calendar' && (
                <section className="space-y-4">
                  {/* Month Navigation Header */}
                  <div className="flex items-center justify-between rounded-xl border bg-card p-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2.5">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Calendar View</h3>
                        <p className="text-sm text-muted-foreground">Browse and edit daily prayer times</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 rounded-lg border bg-muted/50 p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigateMonth(-1)}
                        className="h-8 w-8"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[130px] px-2 text-center text-sm font-medium">
                        {formatMonthDisplay(monthKey)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigateMonth(1)}
                        className="h-8 w-8"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Days List */}
                  {loadingMonth ? (
                    <div className="flex items-center justify-center rounded-xl border bg-card py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">Loading prayer times...</p>
                      </div>
                    </div>
                  ) : monthDays.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border bg-card py-16 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                        <Calendar className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="mt-4 font-medium">No days found for this month</p>
                      <p className="mt-1 text-sm text-muted-foreground">Try navigating to a different month</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {monthDays.map(day => {
                        const jTimes = ['1', '2'].map(s => day.jummah?.[s]).filter(Boolean);
                        const hasJummah = jTimes.length > 0;
                        const dayDate = new Date(parseInt(monthKey?.split('-')[0]), parseInt(monthKey?.split('-')[1]) - 1, parseInt(day.day || day.id));
                        const isFriday = dayDate.getDay() === 5;
                        const now = new Date();
                        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                        const isToday = monthKey === currentMonthKey && parseInt(day.day || day.id, 10) === now.getDate();
                        
                        return (
                          <div
                            key={day.id || day.day}
                            ref={isToday ? todayCalendarRowRef : null}
                            className="group rounded-xl border bg-card p-4 transition-all hover:border-primary/50 hover:shadow-md"
                          >
                            <div className="mb-3 flex items-center justify-between">
                              <div className="flex items-start gap-2">
                                {isFriday && (
                                  <span
                                    aria-hidden="true"
                                    className="mt-2 h-4 w-1 shrink-0 rounded-sm bg-primary"
                                  />
                                )}
                                <div>
                                  <p className="font-medium">
                                    {formatCalendarDate(day)}
                                    {isFriday && (
                                      <span className="ms-2 align-middle text-[11px] font-medium uppercase tracking-wide text-primary">
                                        Jumu&rsquo;ah
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{day.islamicDate}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {day.customOverride && (
                                  <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                    Custom
                                  </Badge>
                                )}
                                {isFriday && (
                                  <Badge variant="outline" className="border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                    Jummah
                                  </Badge>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDay(day)}
                                  className="h-8 gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Edit3 className="h-3.5 w-3.5" />
                                  Edit
                                </Button>
                              </div>
                            </div>
                            
                            {/* Prayer Times Grid */}
                            <div className="grid grid-cols-5 gap-3">
                              {['fajr','duhr','asr','maghrib','isha'].map(prayer => (
                                <div key={prayer} className="rounded-lg bg-muted/50 p-2 text-center">
                                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{prayer}</p>
                                  <p className="mt-1 text-sm font-semibold">
                                    {formatTimeWithAMPM(day[prayer]?.adhan, prayer)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatTimeWithAMPM(day[prayer]?.iqama, prayer)}
                                  </p>
                                </div>
                              ))}
                            </div>
                            
                            {/* Jummah Times */}
                            {hasJummah && (
                              <div className="mt-3 flex items-center gap-3 rounded-lg bg-emerald-500/10 p-2.5">
                                <Moon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                  {['1', '2'].map((slot) => {
                                    const jTime = day.jummah?.[slot];
                                    const kTime = day.khutbah?.[slot];
                                    if (!jTime) return null;
                                    return (
                                      <span key={slot} className="text-muted-foreground">
                                        {kTime && (
                                          <>Khutbah <span className="font-medium text-foreground">{formatTimeWithAMPM(kTime, 'jummah')}</span> · </>
                                        )}
                                        Prayer <span className="font-medium text-foreground">{formatTimeWithAMPM(jTime, 'jummah')}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {/* Jummah Tab - Redesigned */}
              {manageSubTab === 'jummah' && (
                <section className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center gap-3 rounded-xl border bg-card p-4">
                    <div className="rounded-lg bg-emerald-500/10 p-2.5">
                      <Moon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Jummah Prayer Times</h3>
                      <p className="text-sm text-muted-foreground">Set khutbah and prayer times for all Fridays</p>
                    </div>
                  </div>

                  {/* First Jummah */}
                  <div className="rounded-xl border bg-card p-6">
                    <div className="mb-4 flex items-center gap-2">
                      <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20">1st</Badge>
                      <h4 className="font-semibold">First Jummah Prayer</h4>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Khutbah Start Time</Label>
                        <Input
                          className="h-11"
                          placeholder="e.g., 12:30 PM"
                          value={khutbahTimes['1'] || ''}
                          onChange={(e) => setKhutbahTimes(prev => ({ ...prev, '1': e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">Optional - when the sermon begins</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Prayer Time</Label>
                        <Input
                          className="h-11"
                          placeholder="e.g., 1:00 PM"
                          value={jummahTimes['1'] || ''}
                          onChange={(e) => setJummahTimes(prev => ({ ...prev, '1': e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">When the Jummah prayer starts</p>
                      </div>
                    </div>
                  </div>

                  {/* Second Jummah */}
                  <div className="rounded-xl border bg-card p-6">
                    <div className="mb-4 flex items-center gap-2">
                      <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">2nd</Badge>
                      <h4 className="font-semibold">Second Jummah Prayer</h4>
                      <span className="text-xs text-muted-foreground">(if applicable)</span>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Khutbah Start Time</Label>
                        <Input
                          className="h-11"
                          placeholder="e.g., 1:30 PM"
                          value={khutbahTimes['2'] || ''}
                          onChange={(e) => setKhutbahTimes(prev => ({ ...prev, '2': e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">Optional - when the sermon begins</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Prayer Time</Label>
                        <Input
                          className="h-11"
                          placeholder="e.g., 2:00 PM"
                          value={jummahTimes['2'] || ''}
                          onChange={(e) => setJummahTimes(prev => ({ ...prev, '2': e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">When the Jummah prayer starts</p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-muted/30 p-4">
                    <Button
                      onClick={async () => {
                        if (!masjidData?.id) return;
                        
                        // Validate times - use Jummah-specific normalizer that auto-fills AM/PM
                        const j1 = jummahTimes['1']?.trim() ? validateAndNormalizeJummahTime(jummahTimes['1']) : null;
                        const j2 = jummahTimes['2']?.trim() ? validateAndNormalizeJummahTime(jummahTimes['2']) : null;
                        const k1 = khutbahTimes['1']?.trim() ? validateAndNormalizeJummahTime(khutbahTimes['1']) : null;
                        const k2 = khutbahTimes['2']?.trim() ? validateAndNormalizeJummahTime(khutbahTimes['2']) : null;
                        
                        // Validation: khutbah requires jummah (khutbah is optional, but if set, jummah must be set)
                        if (k1 && !j1) {
                          setError("First Khutbah time requires a Prayer time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        if (k2 && !j2) {
                          setError("Second Khutbah time requires a Prayer time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        
                        // Check for invalid formats
                        if (jummahTimes['1']?.trim() && !j1) {
                          setError("Invalid format for First Jummah Prayer time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        if (jummahTimes['2']?.trim() && !j2) {
                          setError("Invalid format for Second Jummah Prayer time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        if (khutbahTimes['1']?.trim() && !k1) {
                          setError("Invalid format for First Khutbah time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        if (khutbahTimes['2']?.trim() && !k2) {
                          setError("Invalid format for Second Khutbah time");
                          setTimeout(() => setError(null), 4000);
                          return;
                        }
                        
                        try {
                          setSaving(true);
                          const token = await auth.currentUser.getIdToken(true);
                          
                          // First, save to prayer rules for persistence
                          const settingsResp = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-settings`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const currentSettings = settingsResp.ok ? await settingsResp.json() : {};
                          const currentIqamah = currentSettings.iqamahSettings || {};
                          const currentRules = currentIqamah.prayerRules || {};
                          
                          // Build jummah/khutbah rules as FIXED_TIME entries
                          const jummahRules = [];
                          const khutbahRules = [];
                          if (j1) jummahRules.push({ type: 'FIXED_TIME', time: j1 });
                              if (j2) jummahRules.push({ type: 'FIXED_TIME', time: j2 });
                              if (k1) khutbahRules.push({ type: 'FIXED_TIME', time: k1 });
                              if (k2) khutbahRules.push({ type: 'FIXED_TIME', time: k2 });
                              
                              const updatedIqamah = {
                                ...currentIqamah,
                                prayerRules: {
                                  ...currentRules,
                                  jummah: jummahRules,
                                  khutbah: khutbahRules,
                                },
                              };
                              
                              await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-settings`, {
                                method: 'PUT',
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ iqamahSettings: updatedIqamah }),
                              });
                              
                              // Then apply to all Fridays in current and next month
                              const now = new Date();
                              const computeMonthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
                              const currentMKey = computeMonthKey(now.getFullYear(), now.getMonth() + 1);
                              const nextMonth = now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2;
                              const nextYear = now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear();
                              const nextMKey = computeMonthKey(nextYear, nextMonth);
                              
                              const targetMonths = [currentMKey, nextMKey];
                              let updated = 0;
                              
                              for (const ym of targetMonths) {
                                const monthResp = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/${ym}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (!monthResp.ok) continue;
                                const monthData = await monthResp.json();
                                if (!monthData.ok || !Array.isArray(monthData.days)) continue;
                                
                                for (const day of monthData.days) {
                                  const dayNum = parseInt(day.day, 10);
                                  
                                  // Explicitly set all slots - empty string clears, value sets
                                  const jummahData = {
                                    '1': j1 || '',
                                    '2': j2 || '',
                                  };
                                  const khutbahData = {
                                    '1': k1 || '',
                                    '2': k2 || '',
                                  };
                                  
                                  const dayId = String(dayNum).padStart(2, '0');
                                  await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/${ym}/${dayId}`, {
                                    method: 'PUT',
                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ jummah: jummahData, khutbah: khutbahData }),
                                  });
                                  updated++;
                                }
                              }
                              
                              showSuccess(`Applied to ${updated} day${updated === 1 ? '' : 's'}`);
                              await loadPrayerTimes(masjidData.id);
                              if (monthKey) await fetchMonthData(masjidData.id, monthKey);
                            } catch (err) {
                              console.error('Apply jummah error:', err);
                              setError('Failed to apply Jummah times');
                              setTimeout(() => setError(null), 4000);
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving}
                          className="gap-2"
                        >
                          <Save className="h-4 w-4" />
                          {saving ? 'Applying...' : 'Apply to All Days'}
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!masjidData?.id) return;
                            
                            try {
                              setSaving(true);
                              const token = await auth.currentUser.getIdToken(true);
                              
                              // Clear from prayer rules
                              const settingsResp = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-settings`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              const currentSettings = settingsResp.ok ? await settingsResp.json() : {};
                              const currentIqamah = currentSettings.iqamahSettings || {};
                              const currentRules = currentIqamah.prayerRules || {};
                              
                              const updatedIqamah = {
                                ...currentIqamah,
                                prayerRules: {
                                  ...currentRules,
                                  jummah: [],
                                  khutbah: [],
                                },
                              };
                              
                              await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-settings`, {
                                method: 'PUT',
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                body: JSON.stringify({ iqamahSettings: updatedIqamah }),
                              });
                              
                              // Clear from all days in current and next month
                              const now = new Date();
                              const computeMonthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
                              const currentMKey = computeMonthKey(now.getFullYear(), now.getMonth() + 1);
                              const nextMonth = now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2;
                              const nextYear = now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear();
                              const nextMKey = computeMonthKey(nextYear, nextMonth);
                              
                              const targetMonths = [currentMKey, nextMKey];
                              let cleared = 0;
                              
                              for (const ym of targetMonths) {
                                const monthResp = await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/${ym}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                if (!monthResp.ok) continue;
                                const monthData = await monthResp.json();
                                if (!monthData.ok || !Array.isArray(monthData.days)) continue;
                                
                                for (const day of monthData.days) {
                                  const dayId = String(day.day).padStart(2, '0');
                                  await fetch(`${API_URL}/v1/masjid/${masjidData.id}/prayer-times/${ym}/${dayId}`, {
                                    method: 'PUT',
                                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ jummah: null, khutbah: null }),
                                  });
                                  cleared++;
                                }
                              }
                              
                              setJummahTimes({ 1: '', 2: '' });
                              setKhutbahTimes({ 1: '', 2: '' });
                              showSuccess(`Cleared Jummah/Khutbah from ${cleared} day${cleared === 1 ? '' : 's'}`);
                              await loadPrayerTimes(masjidData.id);
                              if (monthKey) await fetchMonthData(masjidData.id, monthKey);
                            } catch (err) {
                              console.error('Remove jummah error:', err);
                              setError('Failed to remove Jummah times');
                              setTimeout(() => setError(null), 4000);
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving}
                          className="gap-2 text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                          Remove All
                        </Button>
                      </div>
                </section>
              )}
            </div>
          )}

          {/* Edit Day Modal */}
          {editDay && (
            <EditDayModal
              day={editDay}
              onClose={() => setEditDay(null)}
              onSave={async (masjidId, monthKey, dayId, data) => { await saveDay(masjidData.id, monthKey, dayId, data); }}
              masjidId={masjidData?.id}
              monthKey={monthKey}
            />
          )}

          {/* Preview Modal */}
          {previewVisible && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
              <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
                <CardContent className="p-0">
                  <div className="sticky top-0 flex items-center justify-between border-b bg-card p-4">
                    <h3 className="text-lg font-semibold">Preview - Next {previewDays.length} Days</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPreviewVisible(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="max-h-[calc(80vh-60px)] overflow-y-auto p-4 space-y-3">
                    {loadingPreview && (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {!loadingPreview && previewDays.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No preview available</p>
                    )}
                    {!loadingPreview && previewDays.map(d => (
                      <Card key={d.date} className="border-none bg-muted/50">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex justify-between mb-3">
                            <p className="font-medium">{d.date}</p>
                            <p className="text-sm text-muted-foreground">{d.islamicDate}</p>
                          </div>
                          <div className="grid grid-cols-5 gap-2 sm:gap-4">
                            {['fajr','duhr','asr','maghrib','isha'].map(p => (
                              <div key={p} className="text-center">
                                <p className="text-[10px] capitalize text-muted-foreground sm:text-xs">{p}</p>
                                  <p className="text-xs font-medium sm:text-sm">{formatTimeWithAMPM(d[p]?.adhan, p) || '-'}</p>
                                  <p className="text-[10px] text-muted-foreground sm:text-xs">{formatTimeWithAMPM(d[p]?.iqama, p) || '-'}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      {/* Footer */}
      <footer className="border-t backdrop-blur-sm bg-background/30">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <h3 className="mb-4 bg-gradient-to-r from-[#bf9a2f] via-[#e6c86e] to-[#bf9a2f] bg-clip-text text-3xl font-bold text-transparent">
                Muslim+
              </h3>
              <p className="mb-6 max-w-md text-base leading-relaxed text-muted-foreground">
                Connecting Muslims with their community through technology and faith. Find prayer times, discover nearby masajid, stay updated with events
                {halalEnabled ? ", and explore halal restaurants." : "."}
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="gap-1 px-3 py-1">
                  <Shield className="h-3 w-3" />
                  Trusted Platform
                </Badge>
                <Badge variant="outline" className="gap-1 px-3 py-1">
                  <Sparkles className="h-3 w-3" />
                  Verified Content
                </Badge>
                <Badge variant="outline" className="gap-1 px-3 py-1">
                  <Bell className="h-3 w-3" />
                  Real-time Updates
                </Badge>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="mb-4 text-lg font-semibold">Quick Links</h3>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate("/masjids")}
                  className="text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Find Masajid
                </button>
                <button
                  onClick={() => navigate("/events-public")}
                  className="text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Browse Events
                </button>
                <button
                  onClick={() => navigate("/masjid-dashboard")}
                  className="text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate("/policy")}
                  className="text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Privacy Policy
                </button>
              </div>
            </div>

            {/* Resources */}
            <div>
              <h3 className="mb-4 text-lg font-semibold">Resources</h3>
              <div className="flex flex-col gap-3">
                <a
                  href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Download iOS App
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Download Android App
                </a>
                <a
                  href="https://www.instagram.com/muslimplus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  Follow us on Instagram!
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 border-t pt-8">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Muslim+. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <span className="text-xs text-muted-foreground">
                  Made with ❤️ for the Muslim Community
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </AppLayout>
  );
}

// PRO Badge Component
function ProBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">
      <Crown className="h-2.5 w-2.5" />
      Pro
    </span>
  );
}

// PRO Locked Overlay Component
function ProLockedOverlay({ onUpgrade }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-[2px]">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20">
          <Lock className="h-6 w-6 text-amber-500" />
        </div>
        <p className="text-sm font-medium text-foreground">PRO Feature</p>
        <p className="mt-1 text-xs text-muted-foreground">Upgrade to unlock this feature</p>
        <Button
          size="sm"
          className="mt-3 gap-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          onClick={onUpgrade}
        >
          <Crown className="h-3.5 w-3.5" />
          Upgrade to PRO
        </Button>
      </div>
    </div>
  );
}

// Global Min/Max Rules Section Component (PRO feature)
function GlobalRulesSection({ isPro, globalRules, updateGlobalRule }) {
  // Check if any global rules are set
  const hasRulesSet = useMemo(() => {
    if (!globalRules) return false;
    return PRAYER_NAMES.some(prayer => {
      const rule = globalRules[prayer];
      return rule && (rule.minimumTime || rule.maximumTime);
    });
  }, [globalRules]);
  
  // Track initialization to expand section when rules are set
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Expand section on mount if rules are set
  useEffect(() => {
    if (!hasInitialized && globalRules !== undefined) {
      setIsCollapsed(!hasRulesSet);
      setHasInitialized(true);
    }
  }, [hasRulesSet, globalRules, hasInitialized]);

  const formatTime = (time) => {
    if (!time) return null;
    // Parse 24h format and convert to 12h
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return time;
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${period}`;
  };

  return (
    <section className="relative rounded-xl border bg-card">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex w-full items-center justify-between p-4 sm:p-5"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-500/10 p-2">
            <Sliders className="h-4 w-4 text-amber-500" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold">Global Min/Max Rules</h3>
            <p className="text-xs text-muted-foreground">Set absolute minimum and maximum iqama times</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ProBadge />
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className={`relative border-t ${!isPro ? 'opacity-70' : ''}`}>
          {/* Table Layout */}
          <div className="overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr,100px,100px] items-center gap-2 bg-muted/50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid-cols-[1fr,120px,120px] sm:gap-4 sm:px-6">
              <span>Prayer</span>
              <span className="text-center">Minimum</span>
              <span className="text-center">Maximum</span>
            </div>
            
            {/* Table Body */}
            {PRAYER_NAMES.map((prayer, index) => {
              const rule = globalRules?.[prayer] || { minimumTime: null, maximumTime: null };
              const isLast = index === PRAYER_NAMES.length - 1;
              return (
                <div
                  key={prayer}
                  className={`grid grid-cols-[1fr,100px,100px] items-center gap-2 px-4 py-3 sm:grid-cols-[1fr,120px,120px] sm:gap-4 sm:px-6 ${!isLast ? 'border-b' : ''}`}
                >
                  <span className="font-medium">{PRAYER_LABELS[prayer]}</span>
                  <div className="flex justify-center">
                    <div className="relative">
                      <Input
                        type="time"
                        value={rule.minimumTime || ''}
                        onChange={(e) => isPro && updateGlobalRule(prayer, 'minimumTime', e.target.value || null)}
                        disabled={!isPro}
                        className="h-8 w-[88px] text-center text-xs sm:w-[100px] sm:text-sm [&::-webkit-calendar-picker-indicator]:opacity-50"
                      />
                      {rule.minimumTime && (
                        <button
                          onClick={() => isPro && updateGlobalRule(prayer, 'minimumTime', null)}
                          className="absolute -right-1 -top-1 rounded-full bg-muted p-0.5 hover:bg-destructive/20"
                        >
                          <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="relative">
                      <Input
                        type="time"
                        value={rule.maximumTime || ''}
                        onChange={(e) => isPro && updateGlobalRule(prayer, 'maximumTime', e.target.value || null)}
                        disabled={!isPro}
                        className="h-8 w-[88px] text-center text-xs sm:w-[100px] sm:text-sm [&::-webkit-calendar-picker-indicator]:opacity-50"
                      />
                      {rule.maximumTime && (
                        <button
                          onClick={() => isPro && updateGlobalRule(prayer, 'maximumTime', null)}
                          className="absolute -right-1 -top-1 rounded-full bg-muted p-0.5 hover:bg-destructive/20"
                        >
                          <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {!isPro && <ProLockedOverlay onUpgrade={() => {}} />}
        </div>
      )}
    </section>
  );
}

// Ramadan Rules Section Component (PRO feature)
function RamadanRulesSection({ isPro, ramadanRules, ramadanDates, updateRamadanRule, toggleRamadanEnabled }) {
  const RAMADAN_IQAMAH_MODES = [
    { value: 'NONE', label: 'Use Normal Rule' },
    { value: 'OFFSET', label: 'Offset from Adhan' },
    { value: 'FIXED_TIME', label: 'Fixed Time' },
    { value: 'ROUNDED', label: 'Rounded' },
    { value: 'ROUNDED_WEEKLY', label: 'Rounded Weekly' },
    { value: 'SAME_AS_ADHAN', label: 'Same as Adhan' },
  ];

  const WEEK_START_DAYS = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
  ];

  const prayerColors = {
    fajr: { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-500/30' },
    duhr: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30' },
    asr: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/30' },
    maghrib: { bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-500/30' },
    isha: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/30' },
  };

  // Check if Ramadan is enabled (even without specific prayer rules)
  const isRamadanEnabled = ramadanRules?.enabled || false;
  
  // Track initialization to expand section when rules are set
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  
  // Expand section on mount if Ramadan rules are enabled
  useEffect(() => {
    if (!hasInitialized && ramadanRules !== undefined) {
      setIsCollapsed(!isRamadanEnabled);
      setHasInitialized(true);
    }
  }, [isRamadanEnabled, ramadanRules, hasInitialized]);
  
  // Get display dates
  const currentYear = new Date().getFullYear();
  
  const hasRamadanPassed = (dates) => {
    if (!dates || !dates.endDate) return false;
    const today = new Date();
    const [endMonth, endDay] = dates.endDate.split('-').map(Number);
    const endDate = new Date(today.getFullYear(), endMonth - 1, endDay);
    return today > endDate;
  };
  
  const displayDates = useMemo(() => {
    // First try to use backend-provided dates
    if (ramadanDates && ramadanDates.hijriYear) {
      if (hasRamadanPassed(ramadanDates)) {
        const nextYearDates = getRamadanDatesForYear(currentYear + 1);
        if (nextYearDates?.hijriYear) return nextYearDates;
      }
      return ramadanDates;
    }
    
    // Fallback to local calculation
    let calculated = getRamadanDatesForYear(currentYear);
    if (calculated && hasRamadanPassed(calculated)) {
      const nextYearDates = getRamadanDatesForYear(currentYear + 1);
      if (nextYearDates?.hijriYear) calculated = nextYearDates;
    }
    return calculated || {
      hijriYear: currentYear - 579,
      startDisplay: 'Loading...',
      endDisplay: 'Loading...',
      startDate: '03-01',
      endDate: '03-30'
    };
  }, [ramadanDates, currentYear]);

  const getRuleSummary = (rule) => {
    if (rule.mode === 'NONE') return 'Use Normal Rule';
    if (rule.mode === 'FIXED_TIME' && rule.time) return `Fixed at ${rule.time}`;
    if (rule.mode === 'OFFSET') return `+${rule.offsetMinutes || 0} min`;
    if (rule.mode === 'SAME_AS_ADHAN') return 'Same as Adhan';
    if (rule.mode === 'ROUNDED' || rule.mode === 'ROUNDED_WEEKLY') {
      return `Round to ${rule.intervalMinutes || 5} min`;
    }
    return RAMADAN_IQAMAH_MODES.find(m => m.value === rule.mode)?.label || rule.mode;
  };

  return (
    <section className="relative rounded-xl border bg-card">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex w-full items-center justify-between p-4 sm:p-5"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-purple-500/10 p-2">
            <Moon className="h-4 w-4 text-purple-500" />
          </div>
          <div className="text-left">
            <h3 className="text-base font-semibold">Ramadan Rules</h3>
            <p className="text-xs text-muted-foreground">Override iqama rules during Ramadan</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ProBadge />
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className={`relative border-t px-4 pb-4 sm:px-6 sm:pb-6 ${!isPro ? 'opacity-70' : ''}`}>
          {/* Ramadan Dates Info */}
          <div className="mt-4 rounded-lg border bg-purple-500/5 p-3">
            <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
              <Moon className="h-4 w-4" />
              <span className="font-semibold">Ramadan {displayDates.hijriYear} AH</span>
            </div>
            <p className="mt-1 ml-6 text-sm text-muted-foreground">
              {displayDates.startDisplay} — {displayDates.endDisplay}
            </p>
          </div>

          {/* Enable Toggle */}
          <div className="mt-4 flex items-center justify-between rounded-lg border p-3 sm:p-4">
            <div>
              <p className="font-medium text-sm sm:text-base">Enable Ramadan Rules</p>
              <p className="text-xs text-muted-foreground">Override normal rules during Ramadan</p>
            </div>
            <Switch
              checked={ramadanRules?.enabled || false}
              onCheckedChange={(checked) => isPro && toggleRamadanEnabled(checked)}
              disabled={!isPro}
            />
          </div>

          {/* Prayer Rules - Match IqamahRuleEditor style */}
          {ramadanRules?.enabled && (
            <div className="mt-4 space-y-3">
              {PRAYER_NAMES.map((prayer) => {
                const rule = ramadanRules?.[prayer] || { mode: 'NONE' };
                const isExpanded = expandedPrayer === prayer;
                const colors = prayerColors[prayer];
                
                return (
                  <div key={prayer} className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}>
                    {/* Prayer Header */}
                    <button
                      onClick={() => setExpandedPrayer(isExpanded ? null : prayer)}
                      className="flex w-full items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
                        <span className={`font-semibold ${colors.text}`}>{PRAYER_LABELS[prayer]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{getRuleSummary(rule)}</span>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-4 rounded-lg border bg-card p-4 space-y-4">
                        {/* Mode Selection - Grid buttons like IqamahRuleEditor */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mode</Label>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {RAMADAN_IQAMAH_MODES.map((mode) => (
                              <button
                                key={mode.value}
                                onClick={() => isPro && updateRamadanRule(prayer, 'mode', mode.value)}
                                disabled={!isPro}
                                className={`rounded-lg border p-2.5 text-left text-sm transition-all ${
                                  rule.mode === mode.value
                                    ? 'border-primary bg-primary/10 font-medium'
                                    : 'border-border hover:border-primary/50'
                                } ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {mode.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* NONE mode hint */}
                        {rule.mode === 'NONE' && (
                          <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                            This prayer will use its normal iqama rule during Ramadan.
                          </p>
                        )}

                        {/* SAME_AS_ADHAN mode hint */}
                        {rule.mode === 'SAME_AS_ADHAN' && (
                          <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                            Iqama will be at the same time as adhan (no delay).
                          </p>
                        )}

                        {/* OFFSET mode */}
                        {rule.mode === 'OFFSET' && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Offset (minutes after adhan)</Label>
                            <div className="flex gap-2">
                              {[5, 10, 15, 20, 30].map((mins) => (
                                <Button
                                  key={mins}
                                  size="sm"
                                  variant={rule.offsetMinutes === mins ? 'default' : 'outline'}
                                  onClick={() => isPro && updateRamadanRule(prayer, 'offsetMinutes', mins)}
                                  disabled={!isPro}
                                  className="flex-1"
                                >
                                  +{mins}
                                </Button>
                              ))}
                            </div>
                            <Input
                              type="number"
                              className="h-9"
                              placeholder="Custom offset"
                              value={rule.offsetMinutes ?? ''}
                              onChange={(e) => isPro && updateRamadanRule(prayer, 'offsetMinutes', e.target.value === '' ? null : Number(e.target.value))}
                              disabled={!isPro}
                            />
                          </div>
                        )}

                        {/* FIXED_TIME mode */}
                        {rule.mode === 'FIXED_TIME' && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Iqamah Time</Label>
                            <Input
                              className="h-10"
                              placeholder="e.g., 1:15 PM"
                              value={rule.time || ''}
                              onChange={(e) => isPro && updateRamadanRule(prayer, 'time', e.target.value)}
                              onBlur={(e) => {
                                const normalized = validateAndNormalizeTime(e.target.value);
                                if (normalized && isPro) {
                                  updateRamadanRule(prayer, 'time', normalized);
                                }
                              }}
                              disabled={!isPro}
                            />
                            <p className="text-xs text-muted-foreground">Enter time like "1:15 PM" or "13:15"</p>
                          </div>
                        )}

                        {/* ROUNDED and ROUNDED_WEEKLY modes */}
                        {(rule.mode === 'ROUNDED' || rule.mode === 'ROUNDED_WEEKLY') && (
                          <div className="space-y-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Round to interval</Label>
                                <div className="flex gap-2">
                                  {[5, 10, 15].map((mins) => (
                                    <Button
                                      key={mins}
                                      size="sm"
                                      variant={rule.intervalMinutes === mins ? 'default' : 'outline'}
                                      onClick={() => isPro && updateRamadanRule(prayer, 'intervalMinutes', mins)}
                                      disabled={!isPro}
                                      className="flex-1"
                                    >
                                      {mins}m
                                    </Button>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Then add offset</Label>
                                <Input
                                  type="number"
                                  className="h-9"
                                  placeholder="Minutes"
                                  value={rule.offsetMinutes ?? ''}
                                  onChange={(e) => isPro && updateRamadanRule(prayer, 'offsetMinutes', e.target.value === '' ? null : Number(e.target.value))}
                                  disabled={!isPro}
                                />
                              </div>
                            </div>

                            {rule.mode === 'ROUNDED_WEEKLY' && (
                              <div className="space-y-2">
                                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Week starts on</Label>
                                <div className="flex gap-1">
                                  {WEEK_START_DAYS.map((day) => (
                                    <Button
                                      key={day.value}
                                      size="sm"
                                      variant={(rule.weekStartDay ?? 0) === day.value ? 'default' : 'outline'}
                                      onClick={() => isPro && updateRamadanRule(prayer, 'weekStartDay', day.value)}
                                      disabled={!isPro}
                                      className="flex-1 text-xs"
                                    >
                                      {day.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {!isPro && <ProLockedOverlay onUpgrade={() => {}} />}
        </div>
      )}
    </section>
  );
}

// Iqamah Rule Editor Component - Redesigned
function IqamahRuleEditor({ prayer, rules, updateRule, addRule, removeRule }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const PRAYER_LABELS = { fajr: 'Fajr', duhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' };
  const IQAMAH_MODES = [
    { value: 'OFFSET', label: 'Offset from Adhan', description: 'Add minutes after adhan' },
    { value: 'FIXED_TIME', label: 'Fixed Time', description: 'Set a specific time' },
    { value: 'ROUNDED', label: 'Rounded', description: 'Round to nearest interval' },
    { value: 'ROUNDED_WEEKLY', label: 'Rounded Weekly', description: 'Round with weekly reset' },
    { value: 'SAME_AS_ADHAN', label: 'Same as Adhan', description: 'No delay after adhan' },
  ];

  const WEEK_START_DAYS = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
  ];

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    let m, d;
    if (dateStr.length === 5) {
      [m, d] = dateStr.split('-').map(Number);
    } else {
      const parts = dateStr.split('-');
      m = parseInt(parts[1], 10);
      d = parseInt(parts[2], 10);
    }
    if (!Number.isFinite(m) || !Number.isFinite(d)) {
      return dateStr;
    }
    return `${m}/${d}`;
  };

  const getRuleSummary = (rule) => {
    const mode = IQAMAH_MODES.find((m) => m.value === rule.mode)?.label || rule.mode;
    let detail = '';
    if (rule.mode === 'OFFSET' && rule.offsetMinutes != null) {
      detail = ` (+${rule.offsetMinutes} min)`;
    } else if (rule.mode === 'FIXED_TIME' && rule.time) {
      detail = ` at ${rule.time}`;
    }
    return `${mode}${detail}`;
  };

  const prayerColors = {
    fajr: { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-500/30' },
    duhr: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30' },
    asr: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/30' },
    maghrib: { bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-500/30' },
    isha: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/30' },
  };

  const colors = prayerColors[prayer];

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
          <span className={`font-semibold ${colors.text}`}>{PRAYER_LABELS[prayer]}</span>
          <span className="text-xs text-muted-foreground">
            {rules.length} rule{rules.length !== 1 ? 's' : ''}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addRule(prayer)}
          className={`h-8 gap-1.5 ${colors.text} hover:${colors.bg}`}
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>
      
      <div className="space-y-2">
        {rules.map((rule, idx) => (
          <div key={idx} className="rounded-lg border bg-card overflow-hidden">
            <button
              className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50 transition-colors"
              onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{getRuleSummary(rule)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDateForDisplay(rule.startDate) || '1/1'} – {formatDateForDisplay(rule.endDate) || '12/31'}
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expandedIndex === idx ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedIndex === idx && (
              <div className="border-t bg-muted/30 p-4 space-y-4">
                {/* Mode Selection */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mode</Label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {IQAMAH_MODES.slice(0, 5).map((mode) => (
                      <button
                        key={mode.value}
                        onClick={() => updateRule(prayer, idx, 'mode', mode.value)}
                        className={`rounded-lg border p-2.5 text-left text-sm transition-all ${
                          rule.mode === mode.value
                            ? 'border-primary bg-primary/10 font-medium'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mode-specific options */}
                {rule.mode === 'OFFSET' && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Offset (minutes after adhan)</Label>
                    <div className="flex gap-2">
                      {[5, 10, 15, 20, 30].map((mins) => (
                        <Button
                          key={mins}
                          size="sm"
                          variant={rule.offsetMinutes === mins ? 'default' : 'outline'}
                          onClick={() => updateRule(prayer, idx, 'offsetMinutes', mins)}
                          className="flex-1"
                        >
                          +{mins}
                        </Button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      className="h-9"
                      placeholder="Custom offset"
                      value={rule.offsetMinutes ?? ''}
                      onChange={(e) => updateRule(prayer, idx, 'offsetMinutes', e.target.value === '' ? null : Number(e.target.value))}
                    />
                  </div>
                )}

                {rule.mode === 'FIXED_TIME' && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Iqamah Time</Label>
                    <Input
                      className="h-10"
                      placeholder="e.g., 1:15 PM"
                      value={rule.time || ''}
                      onChange={(e) => updateRule(prayer, idx, 'time', e.target.value)}
                      onBlur={(e) => {
                        const normalized = validateAndNormalizeTime(e.target.value);
                        if (normalized) {
                          updateRule(prayer, idx, 'time', normalized);
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground">Enter time like "1:15 PM" or "13:15"</p>
                  </div>
                )}

                {(rule.mode === 'ROUNDED' || rule.mode === 'ROUNDED_WEEKLY') && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Round to interval</Label>
                      <div className="flex gap-2">
                        {[5, 10, 15].map((mins) => (
                          <Button
                            key={mins}
                            size="sm"
                            variant={rule.intervalMinutes === mins ? 'default' : 'outline'}
                            onClick={() => updateRule(prayer, idx, 'intervalMinutes', mins)}
                            className="flex-1"
                          >
                            {mins}m
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Then add offset</Label>
                      <Input
                        type="number"
                        className="h-9"
                        placeholder="Minutes"
                        value={rule.offsetMinutes ?? ''}
                        onChange={(e) => updateRule(prayer, idx, 'offsetMinutes', e.target.value === '' ? null : Number(e.target.value))}
                      />
                    </div>
                  </div>
                )}

                {rule.mode === 'ROUNDED_WEEKLY' && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Week starts on</Label>
                    <div className="flex gap-1">
                      {WEEK_START_DAYS.map((day) => (
                        <Button
                          key={day.value}
                          size="sm"
                          variant={(rule.weekStartDay ?? 0) === day.value ? 'default' : 'outline'}
                          onClick={() => updateRule(prayer, idx, 'weekStartDay', day.value)}
                          className="flex-1 text-xs"
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Date Range */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Date Range (optional)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        className="h-9"
                        placeholder="Start (MM/DD)"
                        value={rule.startDate || ''}
                        onChange={(e) => updateRule(prayer, idx, 'startDate', e.target.value || null)}
                      />
                    </div>
                    <div>
                      <Input
                        className="h-9"
                        placeholder="End (MM/DD)"
                        value={rule.endDate || ''}
                        onChange={(e) => updateRule(prayer, idx, 'endDate', e.target.value || null)}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Leave empty for year-round. Use for seasonal schedules.</p>
                </div>

                {/* Remove Button */}
                {rules.length > 1 && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRule(prayer, idx)}
                      className="h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Remove this rule
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Edit Day Modal
function EditDayModal({ day, onClose, onSave, masjidId, monthKey }) {
  const [local, setLocal] = useState(day || {});
  const [modalError, setModalError] = useState("");
  useEffect(() => { setLocal(day || {}); }, [day]);

  if (!day) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b p-4 sticky top-0 bg-card z-10">
            <h3 className="text-lg font-semibold">Edit Day {day.id}</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="p-4 space-y-3 sm:space-y-4">
            {modalError && (
              <Card className="border-destructive/40 bg-destructive/10">
                <CardContent className="p-3 text-sm text-destructive">
                  {modalError}
                </CardContent>
              </Card>
            )}
            {["fajr", "duhr", "asr", "maghrib", "isha"].map(p => (
              <div key={p} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <p className="w-20 shrink-0 font-medium capitalize">{p}</p>
                <div className="flex flex-1 gap-2">
                  <Input
                    placeholder="Adhan"
                    value={local[p]?.adhan || ''}
                    onChange={(e) => setLocal(prev => ({ ...prev, [p]: { ...(prev[p] || {}), adhan: e.target.value } }))}
                  />
                  <Input
                    placeholder="Iqamah"
                    value={local[p]?.iqama || ''}
                    onChange={(e) => setLocal(prev => ({ ...prev, [p]: { ...(prev[p] || {}), iqama: e.target.value } }))}
                  />
                </div>
              </div>
            ))}
            
            {/* Jummah/Khutbah Section */}
            <div className="border-t pt-4 mt-4">
              <p className="font-medium mb-3">Jummah & Khutbah</p>
              <div className="space-y-3">
                {["1", "2"].map(slot => (
                  <div key={`jummah-${slot}`} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <p className="w-20 shrink-0 text-sm text-muted-foreground">{slot === "1" ? "First" : "Second"}</p>
                    <div className="flex flex-1 gap-2">
                      <Input
                        placeholder="Khutbah"
                        value={local.khutbah?.[slot] || ''}
                        onChange={(e) => setLocal(prev => ({
                          ...prev,
                          khutbah: { ...(prev.khutbah || {}), [slot]: e.target.value }
                        }))}
                      />
                      <Input
                        placeholder="Jummah"
                        value={local.jummah?.[slot] || ''}
                        onChange={(e) => setLocal(prev => ({
                          ...prev,
                          jummah: { ...(prev.jummah || {}), [slot]: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t p-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const dayKeyRaw = day?.id || day?.day;
                if (!dayKeyRaw) {
                  setModalError("Unable to determine the day to update.");
                  return;
                }
                const normalizedDayId = String(dayKeyRaw).padStart(2, "0");

                // Validate times before saving
                const updated = {};
                const prayers = ["fajr", "duhr", "asr", "maghrib", "isha"];
                for (const p of prayers) {
                  const adhanRaw = local[p]?.adhan || "";
                  const iqamaRaw = local[p]?.iqama || "";
                  const adhanNorm = adhanRaw.trim() ? validateAndNormalizeTime(adhanRaw) : "";
                  const iqamaNorm = iqamaRaw.trim() ? validateAndNormalizeTime(iqamaRaw) : "";
                  if (adhanRaw.trim() && !adhanNorm) {
                    setModalError(`Invalid adhan time for ${p}. Use HH:MM with optional AM/PM.`);
                    return;
                  }
                  if (iqamaRaw.trim() && !iqamaNorm) {
                    setModalError(`Invalid iqamah time for ${p}. Use HH:MM with optional AM/PM.`);
                    return;
                  }
                  updated[p] = {
                    ...(local[p] || {}),
                    adhan: adhanNorm || "",
                    iqama: iqamaNorm || "",
                  };
                }
                
                // Validate jummah/khutbah pairs
                const jummahData = {};
                const khutbahData = {};
                for (const slot of ["1", "2"]) {
                  const jRaw = local.jummah?.[slot] || "";
                  const kRaw = local.khutbah?.[slot] || "";
                  const jNorm = jRaw.trim() ? validateAndNormalizeTime(jRaw) : "";
                  const kNorm = kRaw.trim() ? validateAndNormalizeTime(kRaw) : "";
                  
                  if (jRaw.trim() && !jNorm) {
                    setModalError(`Invalid Jummah ${slot} time. Use HH:MM with optional AM/PM.`);
                    return;
                  }
                  if (kRaw.trim() && !kNorm) {
                    setModalError(`Invalid Khutbah ${slot} time. Use HH:MM with optional AM/PM.`);
                    return;
                  }
                  
                  // Khutbah requires jummah, but jummah does not require khutbah
                  if (!jNorm && kNorm) {
                    setModalError(`Khutbah ${slot} requires a Jummah Prayer time.`);
                    return;
                  }
                  
                  if (jNorm) jummahData[slot] = jNorm;
                  if (kNorm) khutbahData[slot] = kNorm;
                }
                
                setModalError("");
                if (onSave) await onSave(masjidId, monthKey, normalizedDayId, {
                  ...local,
                  ...updated,
                  jummah: Object.keys(jummahData).length > 0 ? jummahData : null,
                  khutbah: Object.keys(khutbahData).length > 0 ? khutbahData : null,
                });
              }}
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
